import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { getUserOverviewAsync, setPageSize } from "../../store/Agent/userOverviewSlice";
import { User } from "../../services/restserver-openapi";
import { getUserOverviewStats, UserOverviewStats } from "../../utils/User/user-openapi-hooks";
import {
    billingPeriodUnitGerman,
    getSubscriptionName,
    getTotalPriceWithoutCharges,
    isEarlyAccessSubscription,
    statusColor,
} from "../../utils/subscriptionUtils";

const tableContainerSx = {
    borderRadius: 3,
    border: "none",
    borderColor: "divider",
    boxShadow: "none",
    overflow: "hidden",
    bgcolor: "background.paper",
} as const;

const headTypoSx = {
    textTransform: "uppercase",
    letterSpacing: 0.8,
} as const;

const UserTable = ({ userRole }: { userRole: User.userRole }) => {
    const dispatch = useAppDispatch();

    const userOverview = useAppSelector((state) => state.userOverview.userOverview);
    const pageSize = useAppSelector((state) => state.userOverview.pageSize);
    const totalEntries = useAppSelector((state) => state.userOverview.totalEntries);

    const [filter, setFilter] = useState("");
    const [orderBy, setOrderBy] = useState("userId");
    const [direction, setDirection] = useState<"ASC" | "DESC">("ASC");
    const [pageNumber, setPageNumber] = useState(0);
    const [statsPeriod, setStatsPeriod] = useState<'DAY' | 'WEEK' | 'MONTH' | 'YEAR'>('WEEK');
    const [stats, setStats] = useState<UserOverviewStats | null>(null);
    const [statsError, setStatsError] = useState("");
    const netActiveChange = (stats?.activeChangedSince ?? 0) - (stats?.deletedChangedSince ?? 0);

    useEffect(() => {
        dispatch(getUserOverviewAsync({ userRole, filter, pageNumber, pageSize, direction, orderBy }));
    }, [dispatch, userRole, filter, pageNumber, pageSize, direction, orderBy]);

    useEffect(() => {
        let mounted = true;

        getUserOverviewStats(userRole, statsPeriod)
            .then((response) => {
                if (!mounted) {
                    return;
                }
                setStats(response);
                setStatsError("");
            })
            .catch(() => {
                if (!mounted) {
                    return;
                }
                setStats(null);
                setStatsError("Kennzahlen konnten nicht geladen werden.");
            });

        return () => {
            mounted = false;
        };
    }, [userRole, statsPeriod]);

    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPageNumber(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        dispatch(setPageSize(parseInt(event.target.value, 10)));
        setPageNumber(0);
    };

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1.5 }}>
                    <Typography variant="h6">Kennzahlen</Typography>
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel id="stats-period-label">Vergleichszeitraum</InputLabel>
                        <Select
                            labelId="stats-period-label"
                            label="Vergleichszeitraum"
                            value={statsPeriod}
                            onChange={(event) => setStatsPeriod(event.target.value as 'DAY' | 'WEEK' | 'MONTH' | 'YEAR')}
                        >
                            <MenuItem value="DAY">Tag</MenuItem>
                            <MenuItem value="WEEK">Woche</MenuItem>
                            <MenuItem value="MONTH">Monat</MenuItem>
                            <MenuItem value="YEAR">Jahr</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {statsError && <Alert severity="warning" sx={{ mb: 1.5 }}>{statsError}</Alert>}

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "grey.100",
                            border: "1px solid",
                            borderColor: "grey.300",
                            textAlign: "center",
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">Aktive User</Typography>
                        <Typography variant="h5" fontWeight={800}>{stats?.activeUsers ?? "-"}</Typography>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "grey.100",
                            border: "1px solid",
                            borderColor: "grey.300",
                            textAlign: "center",
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">Gelöschte User</Typography>
                        <Typography variant="h5" fontWeight={800}>{stats?.deletedUsers ?? "-"}</Typography>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "grey.100",
                            border: "1px solid",
                            borderColor: "grey.300",
                            textAlign: "center",
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">Veränderung</Typography>
                        <Typography variant="h5" fontWeight={800} color={netActiveChange < 0 ? "error.main" : "success.main"}>
                            {stats ? `${netActiveChange >= 0 ? "+" : ""}${netActiveChange}` : "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {stats ? `+${stats.activeChangedSince} aktiv / -${stats.deletedChangedSince} gelöscht` : "-"}
                        </Typography>
                    </Paper>
                </Box>
            </Box>

            {/* Controls (optisch clean, passend zum Layout) */}
            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                }}
            >
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <Typography variant="subtitle1" component="span">
                        <Box component="span" fontWeight={900}>
                            {totalEntries}
                        </Box>{" "}
                        Einträge
                    </Typography>

                    <TextField
                        placeholder="Name, E-Mail, Chargebee-ID"
                        label="Search Filter"
                        margin="dense"
                        value={filter}
                        onChange={(e) => {
                            setFilter(e.target.value);
                            setPageNumber(0);
                        }}
                        size="small"
                    />
                </Box>

                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="order-by-label">Sortieren nach</InputLabel>
                        <Select
                            labelId="order-by-label"
                            label="Sortieren nach"
                            value={orderBy}
                            onChange={(e) => {
                                setOrderBy(e.target.value);
                                setPageNumber(0);
                            }}
                        >
                            <MenuItem value={"userId"}>UID</MenuItem>
                            <MenuItem value={"person.firstname"}>Vorname</MenuItem>
                            <MenuItem value={"person.lastname"}>Nachname</MenuItem>
                            <MenuItem value={"person.email"}>E-Mail</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="direction-label">Sortierrichtung</InputLabel>
                        <Select
                            labelId="direction-label"
                            label="Sortierrichtung"
                            value={direction}
                            onChange={(e) => {
                                setDirection(e.target.value as "ASC" | "DESC");
                                setPageNumber(0);
                            }}
                        >
                            <MenuItem value={"ASC"}>Aufsteigend</MenuItem>
                            <MenuItem value={"DESC"}>Absteigend</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Table (genau gleicher Style wie CompoundTable) */}
            <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow sx={{ "& th": { bgcolor: "background.paper", borderBottomColor: "divider" } }}>
                            {["UID", "Vorname", "Nachname", "E-Mail", "Abo", "Page"].map((h) => (
                                <TableCell key={h}>
                                    <Typography
                                        color="text.secondary"
                                        variant="caption"
                                        fontWeight={900}
                                        sx={headTypoSx}
                                    >
                                        {h}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {userOverview.map((user) => (
                            <TableRow
                                key={`${user.userId}-${user.userRole}`}
                                hover
                                sx={{
                                    // "& td": { borderBottomColor: "divider" },
                                    // "&:nth-of-type(2n)": { bgcolor: "alternate.main" },
                                }}
                            >
                                <TableCell>{user.userId}</TableCell>
                                <TableCell>{user.firstName}</TableCell>
                                <TableCell>{user.lastName}</TableCell>
                                <TableCell>{user.email}</TableCell>

                                <TableCell>
                                    {user.subscription && user.subscription.id ? (
                                        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                                            <Chip size="small" color={statusColor(user.subscription.status)} />
                                            <Box>
                                                <Link
                                                    href={`https://${process.env.REACT_APP_CHARGEBEE_SITE}.chargebee.com/d/subscriptions/${user.subscription.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    underline="hover"
                                                    sx={{ color: "text.primary", fontWeight: 700 }}
                                                >
                                                    {getSubscriptionName(user.subscription)}
                                                </Link>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {getTotalPriceWithoutCharges(user.subscription)}
                                                    {!isEarlyAccessSubscription(user.subscription)
                                                        ? ` / ${billingPeriodUnitGerman(user.subscription.billingPeriodUnit)}`
                                                        : ""}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Typography color="text.secondary">—</Typography>
                                    )}
                                </TableCell>

                                <TableCell>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<InfoIcon />}
                                        sx={{ borderRadius: 2, fontWeight: 800 }}
                                        component={RouterLink}
                                        to={`/travelAgent/user/${user.userId}/${user.userRole}`}
                                    >
                                        Details
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}

                        {userOverview.length === 0 && (
                            <TableRow sx={{ "& td": { borderBottomColor: "divider" } }}>
                                <TableCell colSpan={6}>
                                    <Typography color="text.secondary">Keine Resultate.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={6} sx={{ borderBottom: "none" }}>
                                <TablePagination
                                    component="div"
                                    count={totalEntries}
                                    page={pageNumber}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={pageSize}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                                    labelRowsPerPage="Einträge pro Seite"
                                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
                                    sx={{
                                        "& .MuiTablePagination-toolbar": { px: 1.5 },
                                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                                            color: "text.secondary",
                                        },
                                    }}
                                />
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default UserTable;
